import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  NgZone,
} from '@angular/core';

import { deepEqual } from 'src/app/shared/helpers/object.helper';
import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { UserComponentsService } from '../../template-components/services/user-components.service';
import { BrandingService } from '../../template-components/services/branding.service';
import { ImageCropService } from '../../services/image-crop.service';

type EditorSidebarItemId =
  | 'layers'
  | 'brand'
  | 'text'
  | 'images'
  | 'components';

type EditorSidebarItem = {
  id: EditorSidebarItemId;
  label: string;
  ariaLabel: string;
  icon: string;
  activeIcon?: string;
  activeColor: string;
};

type TextPreset = {
  id: 'heading' | 'subheading' | 'body';
  label: string;
  value: string;
  fontsize: number;
  formatting?: string;
};

@Component({
  selector: 'template-attribute-editor',
  templateUrl: './template-attribute-editor.component.html',
  styleUrls: ['./template-attribute-editor.component.scss'],
  standalone: false,
})
export class TemplateAttributeEditorComponent implements OnDestroy {
  private handleMessageBind;
  private activeSidebarItem: EditorSidebarItemId | null = 'layers';
  private isMobileSidebarLayout = false;
  public componentsFactory = inject(ComponentsService);
  private ngZone = inject(NgZone);
  public attributeDataService = inject(AttributeDataService);
  public userComponentsService = inject(UserComponentsService);
  private brandingService = inject(BrandingService);
  private templateEditorService = inject(TemplateEditorService);
  private imageCropService = inject(ImageCropService);

  sidebarItems: EditorSidebarItem[] = [
    {
      id: 'layers',
      label: 'Layers',
      ariaLabel: 'Show layers',
      icon: 'editor-layers',
      activeIcon: 'editor-layers-filled',
      activeColor: '#2563eb',
    },
    {
      id: 'brand',
      label: 'Brand',
      ariaLabel: 'Edit brand settings',
      icon: 'branding-palette',
      activeColor: '#eab308',
    },
    {
      id: 'text',
      label: 'Text',
      ariaLabel: 'Add text',
      icon: 'editor-text',
      activeColor: '#00a6f4',
    },
    {
      id: 'images',
      label: 'Images',
      ariaLabel: 'Add image',
      icon: 'image',
      activeColor: '#22c55e',
    },
    {
      id: 'components',
      label: 'Components',
      ariaLabel: 'Add component',
      icon: 'editor-components',
      activeColor: '#a855f7',
    },
  ];

  textPresets: TextPreset[] = [
    {
      id: 'heading',
      label: 'Add a heading',
      value: 'Add a heading',
      fontsize: 72,
      formatting: 'bold',
    },
    {
      id: 'subheading',
      label: 'Add a subheading',
      value: 'Add a subheading',
      fontsize: 44,
    },
    {
      id: 'body',
      label: 'Add some body text',
      value: 'Add some body text',
      fontsize: 28,
    },
  ];

  constructor() {
    this.componentsFactory.reset();
    this.syncSidebarLayout();

    this.handleMessageBind = this._handleMessageFromTemplate.bind(this);

    window.addEventListener('message', this.handleMessageBind);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleMessageBind);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncSidebarLayout();
  }

  getActiveSidebarItem(): EditorSidebarItemId | null {
    if (this.componentsFactory.showAttributeList) {
      return 'layers';
    }

    const selected = this.componentsFactory.selected;
    const selectedType = selected?.type || selected?.tagName;

    if (selectedType === 'rise-playlist-item-selector') {
      return 'components';
    }

    if (selectedType === 'rise-branding') {
      return 'brand';
    }

    if (selectedType === 'rise-text') {
      return 'text';
    }

    if (
      selectedType === 'rise-folder' ||
      selectedType === 'rise-stock-library'
    ) {
      return this.getSelectedFolderType(selected) === 'canva'
        ? 'components'
        : 'images';
    }

    return this.activeSidebarItem;
  }

  private getSelectedFolderType(selected: {
    id?: string;
    attributes?: { type?: string };
    options?: { fileType?: string };
  }): string | null {
    if (!selected) {
      return null;
    }

    if (selected.id) {
      const attributeType = this.attributeDataService.getAvailableAttributeData(
        selected.id,
        'type',
      );
      if (attributeType != null) {
        return attributeType;
      }
    }

    if (selected.attributes?.type) {
      return selected.attributes.type;
    }

    if (selected.options?.fileType) {
      return selected.options.fileType;
    }

    return null;
  }

  selectSidebarItem(itemId: EditorSidebarItemId): void {
    this.activeSidebarItem = itemId;

    switch (itemId) {
      case 'layers':
        this.componentsFactory.backToList();
        break;
      case 'brand':
        this.editBrandSettings();
        break;
      case 'text':
        this.showTextAddPanel();
        break;
      case 'images':
        this.userComponentsService.addItem('rise-folder', {
          fileType: 'image',
          singleFile: true,
        });
        break;
      case 'components':
        this.componentsFactory.editComponent(
          {
            type: 'rise-playlist-item-selector',
          },
          {
            userComponents: true,
          },
        );
        break;
    }
  }

  isTextAddPanelVisible(): boolean {
    return (
      !this.componentsFactory.showAttributeList &&
      !this.componentsFactory.selected &&
      this.activeSidebarItem === 'text'
    );
  }

  isComponentSettingsVisible(): boolean {
    return (
      !this.componentsFactory.showAttributeList &&
      !!this.componentsFactory.selected &&
      !this.isTextAddPanelVisible()
    );
  }

  isSidebarPanelVisible(): boolean {
    return (
      this.componentsFactory.showAttributeList ||
      this.isTextAddPanelVisible() ||
      this.isComponentSettingsVisible()
    );
  }

  closeSidebarPanel(): void {
    if (!this.isMobileViewport()) {
      this.componentsFactory.backToList();
      return;
    }

    if (this.componentsFactory.selected) {
      this.componentsFactory.backToList();
    }

    this.activeSidebarItem = null;
    this.componentsFactory._showAttributeList(false);
  }

  addTextBox(): void {
    this.userComponentsService.addItem('rise-text');
  }

  addTextPreset(preset: TextPreset): void {
    this.userComponentsService.addItem('rise-text', {
      attributes: {
        value: preset.value,
        fontsize: preset.fontsize,
        formatting: preset.formatting || '',
      },
    });
  }

  private showTextAddPanel(): void {
    if (this.componentsFactory.selected) {
      this.componentsFactory.backToList();
    }

    this.componentsFactory._showAttributeList(false);
  }

  private syncSidebarLayout(): void {
    const isMobileViewport = this.isMobileViewport();

    if (isMobileViewport === this.isMobileSidebarLayout) {
      return;
    }

    this.isMobileSidebarLayout = isMobileViewport;

    if (!isMobileViewport) {
      if (!this.componentsFactory.selected && this.activeSidebarItem === null) {
        this.activeSidebarItem = 'layers';
        this.componentsFactory._showAttributeList(true);
      }

      return;
    }

    if (!this.componentsFactory.selected) {
      this.activeSidebarItem = null;
      this.componentsFactory._showAttributeList(false);
    }
  }

  private isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    );
  }

  private editBrandSettings(): void {
    const brandingComponent = this.brandingService.getBrandingComponent() || {
      type: 'rise-branding',
    };

    this.componentsFactory.editComponent(brandingComponent);
  }

  _handleMessageFromTemplate(event) {
    var data = event.data;

    if ('string' === typeof event.data) {
      try {
        data = JSON.parse(event.data);
      } catch (e) {}
    }

    switch (data.type) {
      case 'deleteComponent':
        this.userComponentsService.removeUserComponent(data.value.id);
        this.componentsFactory.editHighlightedComponent(null);
        this.ngZone.run(() => {});
        break;
      case 'editComponent':
        this.componentsFactory.editHighlightedComponent(data.value);
        this.ngZone.run(() => {});
        break;
      case 'saveComponentPosition': {
        const customization =
          this.attributeDataService.getAttributeData(
            data.value.id,
            'customization',
          ) || {};
        const incomingCustomization = data.value.customization || {};
        const updatedCustomization = {
          ...customization,
          ...incomingCustomization,
        };

        if (this._isDimensionChange(customization, incomingCustomization)) {
          updatedCustomization.userSized = true;
        }

        const sameCustomization = this._isSameCustomization(
          customization,
          updatedCustomization,
        );
        const crop = this._getAcceptedCrop(data.value.id, data.value.crop);

        if (!crop) {
          if (sameCustomization) {
            break;
          }

          this.attributeDataService.setAttributeData(
            data.value.id,
            'customization',
            updatedCustomization,
          );
        } else {
          const sameCrop = deepEqual(
            this.attributeDataService.getAttributeData(data.value.id, 'crop'),
            crop,
          );

          if (sameCustomization && sameCrop) {
            break;
          }

          // One write, so undo / redo restores the box and the crop together
          this.attributeDataService.setMultipleAttributeData(
            data.value.id,
            sameCustomization
              ? { crop }
              : { customization: updatedCustomization, crop },
          );
        }
        this.ngZone.run(() => {});
        break;
      }
      case 'highlightedComponent':
        this.componentsFactory.showContextualToolbar(data.value);
        this.ngZone.run(() => {});
        break;
      case 'rise-text-focus-in':
        this.ngZone.run(() => {
          this.templateEditorService.isInlineEditing = true;
        });
        break;
      case 'rise-text-focus-out':
        this.ngZone.run(() => {
          this.templateEditorService.isInlineEditing = false;
        });
        break;
      case 'undoShortcut':
        this.ngZone.run(() => {
          this.templateEditorService.undo();
        });
        break;
      case 'redoShortcut':
        this.ngZone.run(() => {
          this.templateEditorService.redo();
        });
        break;
      default:
    }
  }

  // common-template's _insertAddedComponents() (rise-user-components.js)
  // falls back to this exact 300x200 box whenever a component has no
  // customization with all of top/left/height/width — the same box a
  // freshly-added layout component sits at when its metadata lookup
  // failed (see UserComponentsService). Used below as the assumed
  // "previous" size when no customization is stored yet.
  private static readonly DEFAULT_BOX_WIDTH = 300;
  private static readonly DEFAULT_BOX_HEIGHT = 200;

  // common-template always reports the full box (top/left/width/height)
  // together on every saveComponentPosition message, for a resize, a plain
  // drag, or an arrow-key nudge alike — so a pure move reports the same
  // width/height it already had. Comparing values (not just key presence)
  // is what distinguishes an actual resize from a move: only a resize
  // should mark the component as manually sized, so later layout switches
  // (see LayoutHugBoxService) stop auto-adjusting it.
  //
  // When there's no stored customization yet, a plain move and a genuine
  // first resize report the identical payload — common-template's
  // saveComponentPosition message carries no signal for which interaction
  // produced it. What breaks the tie is knowing what the component was
  // actually rendered at beforehand: common-template's own default
  // (DEFAULT_BOX_WIDTH/HEIGHT above), not zero. A first move leaves the
  // box at that default, so it compares equal and isn't flagged; a first
  // resize changes it away from the default, so it is.
  private _isSameCustomization(
    previous: Record<string, unknown>,
    updated: Record<string, unknown>,
  ): boolean {
    const positionKeys = ['top', 'left', 'width', 'height'];
    const keys = new Set([
      ...Object.keys(previous || {}),
      ...Object.keys(updated || {}),
    ]);

    for (const key of keys) {
      const previousValue = previous?.[key];
      const updatedValue = updated?.[key];

      if (positionKeys.includes(key)) {
        if (previousValue === undefined || updatedValue === undefined) {
          if (previousValue !== updatedValue) {
            return false;
          }
          continue;
        }

        const previousNumber = parseFloat(previousValue as string) || 0;
        const updatedNumber = parseFloat(updatedValue as string) || 0;
        if (Math.abs(previousNumber - updatedNumber) > 0.5) {
          return false;
        }
        continue;
      }

      if (!deepEqual(previousValue as any, updatedValue as any)) {
        return false;
      }
    }

    return true;
  }

  // A crop sent with a side drag in the preview. Ignored unless the release
  // flag is on, it is valid and the component is a user-added image.
  private _getAcceptedCrop(componentId: string, value: unknown) {
    if (
      value === undefined ||
      !this.imageCropService.isEnabled() ||
      !this.imageCropService.isCroppableComponent(componentId)
    ) {
      return null;
    }

    return this.imageCropService.getValidCrop(value);
  }

  private _isDimensionChange(
    previous: Record<string, unknown>,
    incoming: Record<string, unknown>,
  ): boolean {
    const defaults: Record<string, number> = {
      width: TemplateAttributeEditorComponent.DEFAULT_BOX_WIDTH,
      height: TemplateAttributeEditorComponent.DEFAULT_BOX_HEIGHT,
    };

    return ['width', 'height'].some((key) => {
      if (!(key in incoming)) {
        return false;
      }
      const previousValue =
        previous?.[key] !== undefined
          ? parseFloat(previous[key] as string) || 0
          : defaults[key];
      const incomingValue = parseFloat(incoming[key] as string) || 0;
      // A small tolerance avoids flagging a manual resize from sub-pixel
      // rendering drift on a plain move/reflow.
      return Math.abs(previousValue - incomingValue) > 0.5;
    });
  }
}
